import React, { useEffect, useState } from 'react';
import { MdArrowBack, MdInventory, MdStore, MdTrendingDown, MdTrendingUp, MdLocationOn, MdPhone, MdMail } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { fetchManufacturerRetailerDetail } from '../../../../services/Http/ManufacturerApi';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../../shared/layout/feedback/ErrorMessage';
import StatsGrid from '../../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../../shared/layout/cards/StatCard';
import { useCurrency } from '../../../../context/CurrencyProvider';

const ManufacturerRetailerDetail = () => {
    const { retailerId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const response = await fetchManufacturerRetailerDetail(retailerId);
                setData(response);
            } catch (err) {
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [retailerId]);

    if (loading) {
        return <LoadingMessage>{t('ManufacturerRetailerDetail.loading')}</LoadingMessage>;
    }

    if (error || !data) {
        return <ErrorMessage>{t('ManufacturerRetailerDetail.error')}</ErrorMessage>;
    }

    const { retailer, stats, venues, items } = data;

    const statCards = [
        {
            key: 'inventory',
            value: `${stats.inventory?.toLocaleString() || 0} ${t('ManufacturerRetailerDetail.stats.units')}`,
            label: t('ManufacturerRetailerDetail.stats.inventory'),
            icon: <MdInventory className="icon" />,
            color: 'venues',
        },
        {
            key: 'sold_count',
            value: `${stats.sold_count?.toLocaleString() || 0} ${t('ManufacturerRetailerDetail.stats.units')}`,
            label: t('ManufacturerRetailerDetail.stats.sold'),
            icon: <MdStore className="icon" />,
            color: 'users',
        },
        {
            key: 'sell_in_value',
            value: `${formatResolvedAmount({
                eurAmount: stats.sell_in_value,
                localAmount: stats.sell_in_value_local ?? stats.sell_in_local,
                localCurrencyId: stats.sell_in_currency_id ?? stats.currency_id,
                localCurrencyCode: stats.sell_in_currency_code ?? stats.currency_code,
            })} / ${stats.sell_in_quantity?.toLocaleString() || 0} ${t('ManufacturerRetailerDetail.stats.units')}`,
            label: t('ManufacturerRetailerDetail.stats.buyValue'),
            icon: <MdTrendingDown className="icon" />,
            color: 'transactions',
        },
        {
            key: 'sell_through_value',
            value: `${formatResolvedAmount({
                eurAmount: stats.sell_through_value,
                localAmount: stats.sell_through_value_local ?? stats.sell_through_local,
                localCurrencyId: stats.sell_through_currency_id ?? stats.currency_id,
                localCurrencyCode: stats.sell_through_currency_code ?? stats.currency_code,
            })} / ${stats.sell_through_quantity?.toLocaleString() || 0} ${t('ManufacturerRetailerDetail.stats.units')}`,
            label: t('ManufacturerRetailerDetail.stats.sellValue'),
            icon: <MdTrendingUp className="icon" />,
            color: 'totalVolume',
        },
    ];

    return (
        <PageContainer>
            <PageHeader>
                <BackButton onClick={() => navigate('/retailers')}>
                    <MdArrowBack /> {t('ManufacturerRetailerDetail.back')}
                </BackButton>
                <TitleBlock>
                    <PageTitle>{retailer.name}</PageTitle>
                    <Subtitle>{t('ManufacturerRetailerDetail.subtitle')}</Subtitle>
                </TitleBlock>
            </PageHeader>
            <PageContent>
                <StatsGrid>
                    {statCards.map((card) => (
                        <StatCard
                            key={card.key}
                            icon={card.icon}
                            title={card.label}
                            value={card.value}
                            color={card.color}
                        />
                    ))}
                </StatsGrid>

                <InfoGrid>
                    <InfoCard>
                        <SectionTitle>{t('ManufacturerRetailerDetail.contact.title')}</SectionTitle>
                        <ContactLine><MdMail /> {retailer.email || t('ManufacturerRetailerDetail.contact.notAvailable')}</ContactLine>
                        <ContactLine><MdPhone /> {retailer.phone || t('ManufacturerRetailerDetail.contact.notAvailable')}</ContactLine>
                        {retailer.website && (
                            <ContactLine><a href={retailer.website} target="_blank" rel="noreferrer">{retailer.website}</a></ContactLine>
                        )}
                        <ContactLine><MdLocationOn /> {retailer.address || t('ManufacturerRetailerDetail.contact.notAvailable')}</ContactLine>
                        {retailer.country && <ContactLine>{retailer.country}</ContactLine>}
                    </InfoCard>

                    <InfoCard>
                        <SectionTitle>{t('ManufacturerRetailerDetail.venues.title')}</SectionTitle>
                        {venues.length === 0 && <MutedText>{t('ManufacturerRetailerDetail.venues.empty')}</MutedText>}
                        {venues.length > 0 && (
                            <VenueList>
                                {venues.map((venue) => (
                                    <li key={venue.id}>
                                        <strong>{venue.name}</strong>
                                        <span>{venue.city || ''} {venue.country || ''}</span>
                                        <small>{venue.address}</small>
                                    </li>
                                ))}
                            </VenueList>
                        )}
                    </InfoCard>
                </InfoGrid>

                <TableSection>
                    <SectionTitle>{t('ManufacturerRetailerDetail.items.title')}</SectionTitle>
                    {items.length === 0 ? (
                        <MutedText>{t('ManufacturerRetailerDetail.items.empty')}</MutedText>
                    ) : (
                        <StyledTable>
                            <thead>
                                <tr>
                                    <th>{t('ManufacturerRetailerDetail.items.columns.product')}</th>
                                    <th>{t('ManufacturerRetailerDetail.items.columns.sku')}</th>
                                    <th>{t('ManufacturerRetailerDetail.items.columns.purchased')}</th>
                                    <th>{t('ManufacturerRetailerDetail.items.columns.sold')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => (
                                    <tr key={item.id}>
                                        <td>{item.name}</td>
                                        <td>{item.sku}</td>
                                        <td>
                                            <strong>{item.purchased_quantity}</strong>
                                            <span>{formatResolvedAmount({
                                                eurAmount: item.purchased_value,
                                                localAmount: item.purchased_value_local,
                                                localCurrencyId: item.purchased_currency_id ?? stats.sell_in_currency_id ?? stats.currency_id,
                                                localCurrencyCode: item.purchased_currency_code ?? stats.sell_in_currency_code ?? stats.currency_code,
                                            })}</span>
                                        </td>
                                        <td>
                                            <strong>{item.sold_quantity}</strong>
                        <span>{formatResolvedAmount({
                            eurAmount: item.sold_value,
                            localAmount: item.sold_value_local,
                            localCurrencyId: item.sold_currency_id ?? stats.sell_through_currency_id ?? stats.currency_id,
                            localCurrencyCode: item.sold_currency_code ?? stats.sell_through_currency_code ?? stats.currency_code,
                        })}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </StyledTable>
                    )}
                </TableSection>
            </PageContent>
        </PageContainer>
    );
};

export default ManufacturerRetailerDetail;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    background: none;
    border: none;
    color: rgba(130, 22, 146, 1);
    font-weight: 600;
    cursor: pointer;
`;

const TitleBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const Subtitle = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1.25rem;
    margin-top: 1.875rem;
`;

const InfoCard = styled.div`
    border: 1px solid rgba(130, 22, 146, 0.2);
    border-radius: 12px;
    padding: 1.25rem;
    background: #fff;
`;

const SectionTitle = styled.h3`
    margin-top: 0;
    margin-bottom: 0.75rem;
    color: rgba(130, 22, 146, 1);
`;

const ContactLine = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    color: ${({ theme }) => theme.colors.textPrimary || '#222'};

    a {
        color: inherit;
        text-decoration: underline;
    }
`;

const VenueList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    li {
        border: 1px solid rgba(130, 22, 146, 0.15);
        border-radius: 8px;
        padding: 0.625rem;
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
    }

    strong {
        color: ${({ theme }) => theme.colors.textPrimary || '#222'};
    }

    small {
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
    }
`;

const TableSection = styled.div`
    margin-top: 2.5rem;
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;

    th, td {
        padding: 0.75rem;
        border-bottom: 1px solid rgba(130, 22, 146, 0.1);
        text-align: left;
    }

    th {
        font-weight: 600;
        color: rgba(130, 22, 146, 1);
    }

    td span {
        display: block;
        font-size: 0.875rem;
        color: ${({ theme }) => theme.colors.textSecondary || '#777'};
    }
`;

const MutedText = styled.p`
    color: ${({ theme }) => theme.colors.textSecondary || '#777'};
`;
